<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, markRaw, ref, watch } from 'vue';

import DividerComponent from '@/components/ui/DividerComponent.vue';
import {
  formatCurrency,
  formatFullDate,
  formatInvestmentStatus,
  formatItemValue
} from '@/utils/formatters.ts';
import { InvestmentStatusKey } from "@/enums/bank_enum.ts";
import { displayModal } from "@/utils/modalUtils.ts";
import BankService from "@/services/bank/bankService.ts";
import { displayToast } from "@/utils/toastUtils.ts";
import OperationStatusComponent from "@/components/bank/OperationStatusComponent.vue";

import type { InvestmentOperationDetailResponse } from "@/types/services/bank/bank.type.ts";

const props = defineProps<{
  contractId: number
  drawerMode?: boolean
}>()

const emit = defineEmits<{
  (emit: 'status-updated', payload: {
    contractId: number
    status: InvestmentStatusKey
  }): void
}>()

const { t } = useI18n()

const loading = ref(false)
const investment = ref<InvestmentOperationDetailResponse | null>(null)

const canUpdate = computed(() => {
  return investment.value?.status === InvestmentStatusKey.PAYMENT_PENDING;
})

async function fetchInvestment(contractId: number): Promise<void> {
  loading.value = true
  try {
    investment.value = await BankService.getInvestment(contractId)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.contractId,
  (id) => {
    investment.value = null
    fetchInvestment(id)
  },
  { immediate: true }
)

// --- Expire investment ---
async function expireInvestment() {
  if (!investment.value) return

  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailInvestment.modal.expire.title'),
      message: t('views.detailInvestment.modal.expire.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateInvestmentStatus(investment.value.contractId, InvestmentStatusKey.EXPIRED);

      displayToast(t('views.detailInvestment.expireSuccess'), 'success');
      investment.value.status = InvestmentStatusKey.EXPIRED;
      emitNewStatus();
    } finally {
      loading.value = false;
    }
  }
}

// --- Validate payment ---
async function validatePayment() {
  if (!investment.value) return

  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailInvestment.modal.validate.title'),
      message: t('views.detailInvestment.modal.validate.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateInvestmentStatus(investment.value.contractId, InvestmentStatusKey.VALIDATED);

      displayToast(t('views.detailInvestment.validatedSuccess'), 'success');
      investment.value.status = InvestmentStatusKey.VALIDATED;
      emitNewStatus();
    } finally {
      loading.value = false;
    }
  }
}

// --- Cancel Investment ---
async function cancelInvestment() {
  if (!investment.value) return

  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailInvestment.modal.cancel.title'),
      message: t('views.detailInvestment.modal.cancel.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateInvestmentStatus(investment.value.contractId, InvestmentStatusKey.CANCELED);

      displayToast(t('views.detailInvestment.cancelSuccess'), 'success');
      investment.value.status = InvestmentStatusKey.CANCELED;
      emitNewStatus();
    } finally {
      loading.value = false;
    }
  }
}

function emitNewStatus() {
  if (!investment.value) return

  emit('status-updated', {
    contractId: investment.value.contractId,
    status: investment.value.status
  })
}
</script>

<template>
  <template v-if="investment">
    <v-row align="center" class="details">
      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailInvestment.label.mandate') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ formatItemValue(investment.mandateNames) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailInvestment.label.reference') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value font-weight-bold">
          {{ investment.reference }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailInvestment.label.status') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-chip :color="formatInvestmentStatus(investment.status)" class="text-value">
          {{ t('views.detailInvestment.status.' + investment.status) }}
        </v-chip>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center" class="user-details">
      <v-col cols="12" class="d-flex align-center">
        <v-icon color="primary" class="mr-2" icon="PhUser"/>
        {{ t('views.detailInvestment.subtitle.user') }}
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailInvestment.label.name') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value">
          {{ formatItemValue(investment.username) }}
        </p>
      </v-col>

      <v-col cols="6">
        <p class="text-label">
          {{ t('views.detailInvestment.label.email') }}
        </p>
      </v-col>
      <v-col cols="6" class="text-right">
        <p class="text-value">
          {{ formatItemValue(investment.email) }}
        </p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center">
      <v-col cols="12" class="d-flex align-center">
        <v-icon color="primary" class="mr-2" icon="PhWallet"/>
        {{ t('views.detailInvestment.subtitle.amount') }}
      </v-col>

      <v-col cols="12">
        <v-card elevation="0" class="text-center text-primary font-weight-bold">
          {{ formatCurrency(investment.investmentAmount) }}
        </v-card>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center">
      <v-col cols="12" class="d-flex align-center">
        <v-icon color="primary" class="mr-2" icon="PhCalendar"/>
        {{ t('views.detailInvestment.subtitle.date') }}
      </v-col>

      <v-col cols="12">
        <p class="text-label">
          {{ formatFullDate(investment.createdAt) }}
        </p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row align="center" justify="space-between" class="mt-4">
      <v-col cols="4">
        <!-- Expire button -->
        <v-btn
          block
          v-if="canUpdate"
          :loading="loading"
          :disabled="loading"
          color="blue darken-2"
          type="submit"
          variant="outlined"
          @click="expireInvestment"
        >
          {{ t("views.detailInvestment.buttons.expire") }}
          <template #prepend>
            <v-icon icon="PhHourglass" />
          </template>
        </v-btn>
      </v-col>

      <v-col cols="4">
        <!-- Cancel button -->
        <v-btn
          block
          v-if="canUpdate"
          :loading="loading"
          :disabled="loading"
          color="error"
          type="submit"
          variant="outlined"
          @click="cancelInvestment"
        >
          {{ t("views.detailInvestment.buttons.cancel") }}
          <template #prepend>
            <v-icon icon="PhX" />
          </template>
        </v-btn>
      </v-col>

      <v-col cols="4">
        <!-- Validate button -->
        <v-btn
          block
          v-if="canUpdate"
          :loading="loading"
          :disabled="loading"
          type="submit"
          @click="validatePayment"
        >
          {{ t("views.detailInvestment.buttons.validate") }}
          <template #prepend>
            <v-icon icon="PhCheck" />
          </template>
        </v-btn>
      </v-col>
    </v-row>
  </template>
</template>

<style scoped>
.v-card {
  background-color: #fdfaf3;
}

.text-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.text-value {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.87);
}

.details .v-col {
  padding-top: 6px;
  padding-bottom: 6px;
}

.user-details .v-col:not(:first-child) {
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
